declare const App: {
   mediaManagerV2?: boolean;
};

// A MediaFilters collection name (Libs/MediaFilters.php); 'guide_document'
// resolves to onlyDocuments, so both browse and upload filter to documents.
const DOCUMENT_ACCEPTS = 'guide_document';

/** A document picked from the media library, as the v2 editors store it. */
export type EditorDocument = {
   documentid: number;
   /** The document's title, falling back to its filename. */
   title: string;
   filename: string;
   /**
    * DisplayDocument::getDocumentType() (Objects/DisplayDocument.php).
    * Empty when the picker didn't carry one.
    */
   documentType: string;
};

type MediaItem = {
   getID: () => number;
   getTitle: () => string;
   getFilename: () => string;
   getDocumentType: () => string;
};

/**
 * Opens the media library filtered to documents and resolves with the
 * chosen one, or null when the user cancels.
 *
 * Pages opted into Media Manager v2 (issue #62874) get the React modal;
 * every other page keeps the legacy MooTools modal until the flag rolls
 * out. Both accept 'guide_document', so the library and upload filter to
 * documents only.
 */
export async function browseForDocument(): Promise<EditorDocument | null> {
   if (App !== undefined && App.mediaManagerV2) {
      return browseForDocumentV2();
   }

   // Lazy like the v2 path: the MooTools media manager (and its globals)
   // only loads when a browse actually opens.
   const [{ MediaManager }, { MinimalMediaTarget }] = await Promise.all([
      import('Shared/FrameModules/MediaLibrary/media_manager'),
      import('Shared/FrameModules/MediaLibrary/minimal_target'),
   ]);

   return new Promise<EditorDocument | null>(resolve => {
      const target = new MinimalMediaTarget({
         customAttach: (mediaItem: MediaItem, finished: (old: null, success: boolean) => void) => {
            resolve({
               documentid: mediaItem.getID(),
               title: mediaItem.getTitle() || mediaItem.getFilename(),
               filename: mediaItem.getFilename(),
               documentType: mediaItem.getDocumentType(),
            });
            finished(/* old */ null, /* success */ true);
         },
         accepts: DOCUMENT_ACCEPTS,
      });

      // `browse` is grafted onto MediaManager by its StateMachine at runtime,
      // so TypeScript can't see it on the module's inferred shape.
      (MediaManager as unknown as { browse: (target: unknown) => void }).browse(target);
   });
}

/**
 * The v2 counterpart: open the React modal and adapt the flat media item to
 * the {documentid, title, filename} shape the editors store. Mirrors how
 * activateMediaManager() (media_manager_helpers.ts) adapts v2 items for
 * legacy consumers.
 */
async function browseForDocumentV2(): Promise<EditorDocument | null> {
   const { openMediaManager } = await import('Shared/MediaManager');
   const item = await openMediaManager({ accepts: DOCUMENT_ACCEPTS });

   if (!item || item.documentid === undefined) {
      return null;
   }

   return {
      documentid: item.documentid,
      title: item.title || item.filename || '',
      filename: item.filename ?? '',
      documentType: item.document_type ?? '',
   };
}
